import { useEffect, useMemo, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { useNavigate, useParams } from 'react-router-dom'

import {
  creerRecette,
  filtrerIngredients,
  listerIngredients,
  lireRecette,
  majRecette,
  supprimerRecette,
} from '../../db/repo'
import {
  FODMAP_LEVEL_LABELS,
  UNITES,
  UNITE_LABELS,
  type FodmapLevel,
  type RecipeItem,
  type Unite,
} from '../../domain/types'
import { analyser } from '../../analysis/engine'
import { episodesDerives } from '../../analysis/evenements'
import { expliquerProfil, profilFodmapItems } from '../../analysis/fodmap'
import { VERDICT_LABELS, type Verdict } from '../../analysis/types'
import { db } from '../../db/db'
import { SYMPTOM_LABELS } from '../../domain/types'
import { Puce } from '../composants'

const TON_FODMAP: Record<FodmapLevel, 'ok' | 'alerte' | 'danger' | 'neutre'> = {
  faible: 'ok',
  modere: 'alerte',
  eleve: 'danger',
  inconnu: 'neutre',
}

const TON_VERDICT: Record<Verdict, 'ok' | 'alerte' | 'danger' | 'neutre'> = {
  'piste-serieuse': 'danger',
  'a-surveiller': 'alerte',
  'probablement-non-lie': 'ok',
  'donnees-insuffisantes': 'neutre',
}
import {
  BoutonSupprimer,
  Carte,
  Champ,
  ChampNombre,
  Entete,
  IconeCroix,
  Vide,
} from '../composants'

export function PageRecette() {
  const { id } = useParams()
  const navigate = useNavigate()
  const edition = Boolean(id)

  const ingredients = useLiveQuery(() => listerIngredients(), [])

  const [nom, setNom] = useState('')
  const [portions, setPortions] = useState(1)
  const [items, setItems] = useState<RecipeItem[]>([])
  const [favorite, setFavorite] = useState(false)
  const [notes, setNotes] = useState('')
  const [recherche, setRecherche] = useState('')
  const [avertirEdition, setAvertirEdition] = useState(false)

  useEffect(() => {
    if (!id) return
    void lireRecette(id).then((r) => {
      if (!r) return
      setNom(r.nom)
      setPortions(r.portions)
      setItems(r.items)
      setFavorite(r.favorite)
      setNotes(r.notes ?? '')
      setAvertirEdition(true)
    })
  }, [id])

  const resultats = useMemo(() => {
    if (!recherche.trim()) return []
    const dejaPris = new Set(items.map((i) => i.ingredientId))
    return filtrerIngredients(ingredients ?? [], recherche)
      .filter((i) => !dejaPris.has(i.id))
      .slice(0, 8)
  }, [recherche, ingredients, items])

  // Profil calcule sur la saisie en cours, pas sur la version enregistree :
  // l'indicateur se met a jour pendant qu'on modifie la recette.
  const profil = useMemo(
    () => profilFodmapItems(items, portions, ingredients ?? []),
    [items, portions, ingredients],
  )

  // Analyse des symptomes suivant la consommation de cette recette.
  const donnees = useLiveQuery(
    async () =>
      id
        ? {
            repas: await db.meals.toArray(),
            sensations: await db.sensations.toArray(),
            selles: await db.stools.toArray(),
            journaux: await db.dailyLogs.toArray(),
            ingredients: await db.ingredients.toArray(),
            recettes: await db.recipes.toArray(),
          }
        : undefined,
    [id],
  )

  const associations = useMemo(() => {
    if (!donnees || !id) return undefined
    const { sensations, selles, ...reste } = donnees
    const r = analyser({
      ...reste,
      episodes: episodesDerives(sensations, selles),
      options: { permutations: 500 },
    })
    return r.associations.filter((a) => a.cle === `recette:${id}`)
  }, [donnees, id])

  const nomIngredient = (idIng: string) =>
    ingredients?.find((i) => i.id === idIng)?.nom ?? 'Ingrédient supprimé'

  const enregistrer = async () => {
    if (!nom.trim() || items.length === 0) return
    const data = {
      nom: nom.trim(),
      portions: Math.max(1, portions),
      items,
      favorite,
      archivee: false,
      ...(notes.trim() ? { notes: notes.trim() } : {}),
    }
    if (id) await majRecette(id, data)
    else await creerRecette(data)
    navigate('/bibliotheque')
  }

  return (
    <>
      <Entete titre={edition ? 'Modifier la recette' : 'Nouvelle recette'} retour />

      <main className="contenu">
        {edition && avertirEdition && (
          <div className="encart encart--info">
            Modifier cette recette ne change <strong>aucun repas déjà enregistré</strong> : chaque
            repas a figé sa composition au moment de sa saisie. Vos analyses passées restent
            exactes.
          </div>
        )}

        <Carte>
          <div className="pile">
            <Champ label="Nom">
              <input
                type="text"
                value={nom}
                onChange={(e) => setNom(e.target.value)}
                placeholder="Pâtes à la bolognaise"
              />
            </Champ>

            <Champ
              label="Nombre de portions produites"
              aide="Sert à calculer la quantité réelle quand vous n'en mangez qu'une part."
            >
              <ChampNombre
                min={1}
                valeur={portions}
                onChange={setPortions}
                style={{ maxWidth: 130 }}
              />
            </Champ>

            <button
              type="button"
              className="choix"
              aria-pressed={favorite}
              onClick={() => setFavorite((f) => !f)}
              style={{ alignSelf: 'flex-start' }}
            >
              {favorite ? 'Favorite' : 'Marquer comme favorite'}
            </button>
            <span className="champ__aide">
              Les favorites apparaissent en raccourci sur l'écran d'accueil.
            </span>
          </div>
        </Carte>

        <Carte titre="Ingrédients">
          <Champ label="Ajouter un ingrédient">
            <input
              type="search"
              placeholder="Rechercher..."
              value={recherche}
              onChange={(e) => setRecherche(e.target.value)}
              autoComplete="off"
            />
          </Champ>

          {resultats.length > 0 && (
            <ul className="liste" style={{ marginTop: 10 }}>
              {resultats.map((i) => (
                <li key={i.id}>
                  <button
                    type="button"
                    className="item"
                    onClick={() => {
                      setItems((s) => [...s, { ingredientId: i.id, qte: 100, unite: 'g' }])
                      setRecherche('')
                    }}
                  >
                    <span className="grandir item__principal">{i.nom}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {items.length === 0 ? (
            <Vide>Aucun ingrédient.</Vide>
          ) : (
            <ul className="liste" style={{ marginTop: 12 }}>
              {items.map((it, i) => (
                <li key={`${it.ingredientId}-${i}`} className="carte carte--plate" style={{ padding: 11 }}>
                  <div className="ligne ligne--espacee">
                    <span className="grandir item__principal">{nomIngredient(it.ingredientId)}</span>
                    <button
                      type="button"
                      className="btn btn--fantome btn--petit"
                      onClick={() => setItems((s) => s.filter((_, k) => k !== i))}
                      aria-label="Retirer"
                    >
                      <IconeCroix />
                    </button>
                  </div>
                  <div className="ligne" style={{ marginTop: 8 }}>
                    <ChampNombre
                      decimal
                      min={0}
                      valeur={it.qte}
                      onChange={(v) =>
                        setItems((s) => s.map((x, k) => (k === i ? { ...x, qte: v } : x)))
                      }
                      style={{ maxWidth: 110 }}
                      ariaLabel="Quantité"
                    />
                    <select
                      value={it.unite}
                      onChange={(e) =>
                        setItems((s) =>
                          s.map((x, k) =>
                            k === i ? { ...x, unite: e.target.value as Unite } : x,
                          ),
                        )
                      }
                      style={{ maxWidth: 150 }}
                      aria-label="Unité"
                    >
                      {UNITES.map((u) => (
                        <option key={u} value={u}>
                          {UNITE_LABELS[u]}
                        </option>
                      ))}
                    </select>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Carte>


        {/* --- Charge FODMAP --- */}
        {items.length > 0 && (
          <Carte
            titre="Charge FODMAP par portion"
            action={<Puce ton={TON_FODMAP[profil.niveau]}>{FODMAP_LEVEL_LABELS[profil.niveau]}</Puce>}
          >
            <p className="doux">{expliquerProfil(profil)}</p>

            {profil.familles.length > 0 && (
              <div style={{ marginTop: 14 }}>
                <p className="champ__label" style={{ marginBottom: 7 }}>
                  Familles concernées
                </p>
                <ul className="pile pile--serree" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {profil.familles.map((f) => (
                    <li key={f.famille}>
                      <div className="ligne ligne--espacee" style={{ marginBottom: 3 }}>
                        <span className="doux">{f.libelle}</span>
                        <span className="mono tres-doux">{Math.round(f.part * 100)} %</span>
                      </div>
                      <div className="viz__piste viz">
                        <div
                          className="viz__remplissage"
                          style={{ width: `${Math.max(2, f.part * 100)}%`, background: 'var(--viz-serie)' }}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {profil.contributeurs.length > 0 && (
              <div style={{ marginTop: 14 }}>
                <p className="champ__label" style={{ marginBottom: 7 }}>
                  Ce qui pèse le plus
                </p>
                <div className="puces">
                  {profil.contributeurs.slice(0, 5).map((c) => (
                    <Puce key={c.id} ton={TON_FODMAP[c.niveau]}>
                      {c.nom}
                    </Puce>
                  ))}
                </div>
              </div>
            )}

            <p className="tres-doux" style={{ marginTop: 14 }}>
              Estimation indicative, pondérée par les quantités et ramenée à une portion — un
              soupçon d'ail ne classe donc pas toute la recette. Elle ne remplace pas votre propre
              tolérance : c'est précisément ce que les analyses cherchent à établir.
              {profil.nbInconnus > 0 &&
                ` ${profil.nbInconnus} ingrédient(s) non classé(s) ne sont pas comptés.`}
            </p>
          </Carte>
        )}

        {/* --- Symptomes apres consommation --- */}
        {id && (
          <Carte titre="Symptômes après consommation">
            {associations === undefined ? (
              <p className="doux">Calcul en cours...</p>
            ) : associations.length === 0 ? (
              <p className="doux">
                Cette recette n'a encore été associée à aucun repas enregistré.
              </p>
            ) : (
              <>
                <p className="doux" style={{ marginBottom: 12 }}>
                  Symptômes apparus dans les 6 à 24 h suivant un repas contenant cette recette,
                  comparés aux autres repas.
                </p>
                <ul className="liste">
                  {associations.map((a) => (
                    <li key={a.symptome} className="carte carte--plate" style={{ padding: 11 }}>
                      <div className="ligne ligne--espacee ligne--haut">
                        <span className="item__principal grandir">{SYMPTOM_LABELS[a.symptome]}</span>
                        <Puce ton={TON_VERDICT[a.verdict]}>{VERDICT_LABELS[a.verdict]}</Puce>
                      </div>
                      {a.verdict === 'donnees-insuffisantes' ? (
                        <p className="tres-doux" style={{ marginTop: 5 }}>
                          {a.motifInsuffisance}
                        </p>
                      ) : (
                        <p className="doux" style={{ marginTop: 5 }}>
                          {a.contingence.a} déclenchement(s) sur {a.repasExposes} repas exposés (
                          {Math.round(a.risqueExpose * 100)} %), contre{' '}
                          {Math.round(a.risqueNonExpose * 100)} % sans — risque{' '}
                          <span className="gras mono">
                            {Number.isFinite(a.risqueRelatif) ? `×${a.risqueRelatif.toFixed(1)}` : '∞'}
                          </span>
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
                <p className="tres-doux" style={{ marginTop: 12 }}>
                  Une recette est forcément corrélée à ses propres ingrédients : un résultat ici
                  n'indique pas lequel est en cause. L'onglet Analyses le précise quand les données
                  le permettent.
                </p>
              </>
            )}
          </Carte>
        )}

        <Carte>
          <Champ label="Notes (facultatif)">
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Champ>
        </Carte>

        <button
          type="button"
          className="btn btn--principal btn--large"
          onClick={() => void enregistrer()}
          disabled={!nom.trim() || items.length === 0}
        >
          Enregistrer
        </button>

        {id && (
          <BoutonSupprimer
            titre="Supprimer cette recette ?"
            message="Si elle a déjà servi dans un repas, elle sera archivée plutôt que supprimée, afin de ne pas amputer l'historique."
            onSupprimer={() => {
              void supprimerRecette(id).then(() => navigate('/bibliotheque'))
            }}
          />
        )}
      </main>
    </>
  )
}
